
import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { ContentCopy, Delete, DragIndicator, MoreVert } from "@mui/icons-material";
import { QuestionnaireItemType } from "../../_helpers";
import { useTranslation } from "react-i18next";
import QuestionnaireFormActionToolbar from "./QuestionnaireFormActionToolbar";
import QuestionnaireFormFreeTextTitle from "./QuestionnaireFormFreeTextTitle";
import QuestionnaireQuestionOptions from "./QuestionnaireQuestionOptions";

const QuestionnaireQuestion = ({
  question,
  sectionIndex,
  questionIndex,
  updateQuestion,
  deleteQuestion,
  duplicateQuestion,
  handleAddQuestion,
  handleAddSection,
  handleAddFreeText,
  activeToolbarKey,
  setActiveToolbarKey,
  section,
  error ,
}) => {
  const theme = useTheme();
  const {t} = useTranslation()
  const [anchorEl, setAnchorEl] = useState(null);
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleAnswerChange = (value) => {
    updateQuestion(sectionIndex, questionIndex, { answer: value });
  };

  const questionTypes = [
  { label: t("questionnaire.form.questionType.0"), value: QuestionnaireItemType.Radio },
  { label: t("questionnaire.form.questionType.1"), value: QuestionnaireItemType.Checkbox },
  { label: t("questionnaire.form.questionType.2"), value: QuestionnaireItemType.Dropdown },
  { label: t("questionnaire.form.questionType.3"), value: QuestionnaireItemType.ShortText },
  { label: t("questionnaire.form.questionType.4"), value: QuestionnaireItemType.Paragraph },
  { label: t("questionnaire.form.questionType.5"), value: QuestionnaireItemType.Date},
  { label: t("questionnaire.form.questionType.6"), value: QuestionnaireItemType.Time},
  { label: t("questionnaire.form.questionType.7"), value: QuestionnaireItemType.Signature},
];

 if (question.type === QuestionnaireItemType.FreeText) {
    return (
      <QuestionnaireFormFreeTextTitle
        data={question}
        sectionIndex={sectionIndex}
        questionIndex={questionIndex}
        updateQuestion={updateQuestion}
        deleteQuestion={deleteQuestion}
        duplicateQuestion={duplicateQuestion}
        setActiveToolbarKey={setActiveToolbarKey}
        activeToolbarKey={activeToolbarKey}
        handleAddSection={handleAddSection}
       handleAddFreeText={handleAddFreeText}
       handleAddQuestion={handleAddQuestion}
       error={error}
      />
    );
  }
  return (
    <Card
      sx={{
        borderRadius: 1.5,
        mb: 3,
        overflow: "visible",
        borderLeft: `6px solid ${theme.palette.primary.main}`,
        position:"relative",
        border: error
          ? `1px solid ${theme.palette.error.main}`
          : "1px solid transparent",
      }}
    >
      <CardContent>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <DragIndicator sx={{ cursor: "grab" }} />
          <TextField
            fullWidth
            variant="standard"
            placeholder={t('questionnaire.form.question')}
            value={question.text}
            onChange={(e) =>
              updateQuestion(sectionIndex, questionIndex, {
                text: e.target.value,
              })
            }
          />
          <Select
            size="small"
            value={question.type}
            onChange={(e) => {
              const newType = e.target.value;
              let patch = { type: newType,};
              if (![QuestionnaireItemType.Dropdown,QuestionnaireItemType.Radio,QuestionnaireItemType.Checkbox].includes(newType)) {
                patch = {...patch, options:[]};
              }else {
                if (!question.options || question.options.length === 0) {
                  patch = {
                    ...patch,
                     options: [
                      { option: "", }
                    ]
                  };
                }
              }
              updateQuestion(sectionIndex, questionIndex, patch);
            }}
            sx={{ minWidth: 180 }}
          >
            {questionTypes.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </Stack>

        <QuestionnaireQuestionOptions
          question={question}
          sectionIndex={sectionIndex}
          questionIndex={questionIndex}
          updateQuestion={updateQuestion}
          handleAnswerChange={handleAnswerChange}
        />

        <Divider sx={{ my: 1 }} />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            pr: 1,
            pb: 0.5,
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 0.5 }}>
              <IconButton onClick={handleMenuOpen}>
                  <MoreVert fontSize="small" />
                </IconButton>

                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
                  <MenuItem
                    onClick={(e) => {
                      duplicateQuestion(e);
                      handleMenuClose();
                    }}
                  >
                    <ContentCopy fontSize="small" sx={{ mr: 1 }} /> {t('general.copy')}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      deleteQuestion();
                      handleMenuClose();
                    }}
                  >
                    <Delete disabled={section.questions.length === 1} fontSize="small" sx={{ mr: 1 }} /> {t('general.delete')}
                  </MenuItem>
                </Menu>
              </Box>
              <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1 }}>
                <Tooltip title={t('general.copy')}>
                  <IconButton onClick={duplicateQuestion}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title={t('general.delete')}>
                  <IconButton onClick={deleteQuestion} disabled={section.questions.length === 1}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
                </Box>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Switch
                size="small"
                checked={question.allowFiles || false}
                onChange={(e) =>
                  updateQuestion(sectionIndex, questionIndex, {
                    allowFiles: e.target.checked,
                  })
                }
              />
              <Typography variant="body2">{t('questionnaire.form.allowImageUpload')}</Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body2">{t('general.required')}</Typography>
              <Switch
                size="small"
                checked={question.required || false}
                onChange={(e) =>
                  updateQuestion(sectionIndex, questionIndex, {
                    required: e.target.checked,
                  })
                }
              />
            </Box>
          </Box>
        </Box>

        {error &&
          <Typography color="error" variant="body2" sx={{ m: 1 }}>{error}</Typography>
        } 
      </CardContent>

      {`${sectionIndex}-${questionIndex}` == activeToolbarKey && <QuestionnaireFormActionToolbar
              onAddQuestion={handleAddQuestion}
              onAddSection={handleAddSection}
              onAddFreeText={handleAddFreeText}
            />
      }
      
    </Card>
  );
};

export default QuestionnaireQuestion;
